import ErrorManagement, { addErrorToClass } from "../components/errormanagement";
import { textInputClassName } from "../components/textInput";
import CustomPasswordInput from "../components/passwordInput";
import PasswordRules, { passwordRule } from "../components/passwordRules";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup"
import CustomButton from "../components/button";
import { useContext } from "react";
import { ToastDataContext } from "../context/ToastContextProvider";
import { UserDataContext } from "../context/UserContextProvider";
import { hrSettingsPasswordEndpoint } from "../hr_endpoints";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { classNames } from "../App";

export default function HRSettingsPassword({ }) {

    const { t } = useTranslation();

    const schema = yup.object({
        old_password: yup.string().required(t('settings.passwordRequired')),
        password: yup.string().required(t('settings.passwordRequired')) // Field is required
            .test(...passwordRule("length", t('settings.passwordMinLength')))
            .test(...passwordRule("uppercase", t('settings.passwordUppercase')))
            .test(...passwordRule("special", t('settings.passwordSpecialCharacter'))),
        confirm_new_password: yup.string().required(t('settings.passwordRequired'))
            .oneOf([yup.ref('password'), null], t('settings.passwordMatch'))
    })

    const { register, reset, handleSubmit, watch, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    // The rule checklist fills in while the new password is typed.
    const passwordValue = watch('password', '')

    const toastContext = useContext(ToastDataContext)
    const userContext = useContext(UserDataContext)
    const navigate = useNavigate()

    const onSubmit = async (data) => {
        const response = await fetch(hrSettingsPasswordEndpoint, {
            method: "PUT",
            body: JSON.stringify({
                old_password: data.old_password,
                new_password: data.password
            }),
            headers: {
                Authorization: `Bearer ${userContext.user?.token}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 401) {
            userContext.setUserData({})
            navigate('/login')
            toastContext.error(t('sessionExpired'));
        }

        if (!response.ok) {
            const textError = await response.text();
            if (textError === "old_password_incorrect") {
                toastContext.error(t('settings.oldPasswordIncorrect'));
            } else {
                toastContext.error(textError);
            }
        } else {
            reset()
            toastContext.success(t('settings.updateSuccess'));
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-4">
                <div className="flex flex-row gap-2 flex-wrap">
                    <div>
                        <label htmlFor="old_password" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.oldPassword')}</label>
                        <CustomPasswordInput
                            register={register('old_password')}
                            placeholder={t('settings.oldPassword')}
                            className={classNames(textInputClassName, addErrorToClass(errors.old_password))}
                            autoComplete="current-password"
                            showLabel={t('password.show')}
                            hideLabel={t('password.hide')}
                            fullWidth={false}
                        />
                        <ErrorManagement errorObj={errors.old_password} />
                    </div>
                    <div>
                        <label htmlFor="password" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.newPassword')}</label>
                        <CustomPasswordInput
                            register={register('password')}
                            placeholder={t('settings.newPassword')}
                            className={classNames(textInputClassName, addErrorToClass(errors.password))}
                            autoComplete="new-password"
                            showLabel={t('password.show')}
                            hideLabel={t('password.hide')}
                            fullWidth={false}
                        />
                        <ErrorManagement errorObj={errors.password} />
                    </div>
                    <div>
                        <label htmlFor="confirm_new_password" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.confirmNewPassword')}</label>
                        <CustomPasswordInput
                            register={register('confirm_new_password')}
                            placeholder={t('settings.confirmNewPassword')}
                            className={classNames(textInputClassName, addErrorToClass(errors.confirm_new_password))}
                            autoComplete="new-password"
                            showLabel={t('password.show')}
                            hideLabel={t('password.hide')}
                            fullWidth={false}
                        />
                        <ErrorManagement errorObj={errors.confirm_new_password} />
                    </div>
                </div>
                <PasswordRules
                    password={passwordValue}
                    labels={{
                        length: t('password.ruleLength'),
                        uppercase: t('password.ruleUppercase'),
                        special: t('password.ruleSpecial'),
                    }}
                />
                <CustomButton className={"max-w-[10rem]"} type="submit">{t('settings.save')}</CustomButton>
            </div>
        </form>
    )

}